import { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../auth/AuthContext';
import api from '../lib/api';
import { PageHead, Section, Spinner, ErrorNote, useAsync } from '../components/ui';
import { formatPhoneInput } from '../lib/phone';

/**
 * Registration wizard for Events AND Groups (design/024; Groups added for
 * parity). `kind` picks the loader, labels and endpoint:
 *   kind="event" → POST /events/{id}/register
 *   kind="group" → POST /groups/{id}/register
 *
 * Two steps:
 *   1. Your details — First/Last name, Email, Phone. Fields already on the
 *      Person are shown read-only ("Edit in My Profile"); missing fields
 *      are editable inline and PATCHed to /me on Continue.
 *   2. Confirm — event summary + price; button either joins (free) or
 *      redirects to Stripe (paid). Server contract:
 *        POST /events/{id}/register  →  {mode:"joined"|"checkout", ...}
 *
 * The page is deep-linkable and back-button friendly; step is a piece of
 * local state (no URL param).
 */
const FIELDS = [
  { key: 'firstname', label: 'First Name', type: 'text',  required: true },
  { key: 'lastname',  label: 'Last Name',  type: 'text',  required: true },
  { key: 'email',     label: 'Email',      type: 'email', required: true },
  { key: 'phone',     label: 'Phone Number', type: 'tel', required: true },
];

const KINDS = {
  event: {
    load: (id) => api.getEvent(id),
    register: (id) => api.registerForEvent(id),
    detailPath: (id) => `/event/${id}`,
    name: (e) => e?.request?.name || e?.requestName || 'Event',
    fallback: 'Event',
    freeLabel: 'Free registration',
    confirmTitle: 'Confirm registration',
    confirmCta: 'Confirm registration',
    busyCta: 'Registering…',
  },
  group: {
    load: (id) => api.getGroup(id),
    register: (id) => api.registerForGroup(id),
    detailPath: (id) => `/group/${id}`,
    name: (g) => g?.name || 'Group',
    fallback: 'Group',
    freeLabel: 'Free to join',
    confirmTitle: 'Confirm registration',
    confirmCta: 'Join group',
    busyCta: 'Joining…',
  },
};

export default function EventRegisterPage({ kind = 'event' } = {}) {
  const K = KINDS[kind] || KINDS.event;
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const fromHome = location.state?.from === 'home';
  const { me, refreshMe } = useAuth();

  const { loading, error, data, reload } = useAsync(() => K.load(id), [id, kind]);

  const [step, setStep] = useState(1);
  const [savingProfile, setSavingProfile] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [edits, setEdits] = useState({});   // only for fields missing on Person
  const [errMsg, setErrMsg] = useState(null);

  // Snapshot which fields are locked (present on Person) vs editable (missing).
  const locked = useMemo(() => {
    const out = {};
    FIELDS.forEach(({ key }) => { out[key] = !isBlank(me?.[key]); });
    return out;
  }, [me?.firstname, me?.lastname, me?.email, me?.phone]);

  if (loading) return <div className="page"><Spinner /></div>;
  if (error) return <div className="page"><ErrorNote error={error} onRetry={reload} /></div>;

  const event = data;
  const paid = (event?.priceCents || 0) > 0;
  const priceLabel = paid ? `$${(event.priceCents / 100).toFixed(2)}` : 'Free';

  const setEdit = (k) => (e) => {
    const v = e.target.value;
    setEdits((p) => ({ ...p, [k]: k === 'phone' ? formatPhoneInput(v, p[k]) : v }));
  };

  const allComplete = FIELDS.every(({ key }) =>
    locked[key] || !isBlank(edits[key])
  );

  const backToDetail = () => navigate(K.detailPath(id), { state: fromHome ? { from: 'home' } : undefined });

  const continueToConfirm = async () => {
    if (!allComplete) return;
    // If any locked=false field has a fresh value, PATCH /me first.
    const patch = {};
    FIELDS.forEach(({ key }) => {
      if (!locked[key] && !isBlank(edits[key])) patch[key] = edits[key].trim();
    });
    if (Object.keys(patch).length > 0) {
      setSavingProfile(true);
      try {
        await api.updateMe(patch);
        await refreshMe();
      } catch (e) { setErrMsg(e.message); setSavingProfile(false); return; }
      setSavingProfile(false);
    }
    setErrMsg(null);
    setStep(2);
  };

  const submit = async () => {
    setSubmitting(true); setErrMsg(null);
    try {
      const r = await K.register(id);
      if (r?.mode === 'checkout' && r.url) {
        window.location.assign(r.url);
        return;
      }
      // Free join (or paid caller who was already a member).
      navigate(`${K.detailPath(id)}?registered=1`, { replace: true, state: fromHome ? { from: 'home' } : undefined });
    } catch (e) {
      setErrMsg(e.message);
      setSubmitting(false);
    }
  };

  return (
    <div className="page">
      <PageHead
        breadcrumb={
          <a onClick={backToDetail} style={{ cursor: 'pointer' }}>← {K.name(event)}</a>
        }
        title="Register"
        sub={`Step ${step} of 2 · ${priceLabel}`}
      />

      {step === 1 && (
        <Section title="Your details">
          <p style={{ color: 'var(--muted)', fontSize: '0.875rem', marginTop: 0 }}>
            Locked fields come from your profile. Anything missing you can fill in here — it will be saved to your profile.
          </p>
          {FIELDS.map(({ key, label, type }) => (
            <div className="form-field" key={key}>
              <label>{label} <span className="req">*</span></label>
              {locked[key] ? (
                <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                  <input className="input" value={me?.[key] || ''} disabled style={{ flex: 1 }} />
                  <a onClick={() => navigate('/profile', { state: { returnTo: location.pathname } })}
                     style={{ cursor: 'pointer', fontSize: '0.8125rem', color: 'var(--indigo, #4f46e5)', whiteSpace: 'nowrap' }}>
                    Edit
                  </a>
                </div>
              ) : (
                <input
                  className="input"
                  type={type}
                  value={edits[key] || ''}
                  onChange={setEdit(key)}
                  placeholder={`Enter ${label.toLowerCase()}`}
                  autoComplete={autoCompleteFor(key)}
                />
              )}
            </div>
          ))}
          {errMsg && <div style={{ color: '#991b1b', fontSize: '0.875rem', marginBottom: 8 }}>{errMsg}</div>}
          <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end', marginTop: 8 }}>
            <button className="btn" onClick={backToDetail}>Cancel</button>
            <button className="btn btn-primary" disabled={!allComplete || savingProfile} onClick={continueToConfirm}>
              {savingProfile ? 'Saving…' : 'Continue →'}
            </button>
          </div>
        </Section>
      )}

      {step === 2 && (
        <Section title={K.confirmTitle}>
          <div style={{ marginBottom: 12 }}>
            <div style={{ fontWeight: 700, fontSize: '1.05rem' }}>{K.name(event)}</div>
            {event.date && <div style={{ color: 'var(--muted)', fontSize: '0.875rem' }}>{event.date}{event.beginTime ? ` · ${event.beginTime} CST` : ''}</div>}
            {event.location && <div style={{ color: 'var(--muted)', fontSize: '0.875rem' }}>📍 {event.location}</div>}
          </div>
          <div style={{ background: 'var(--card-soft, #f9fafb)', border: '1px solid var(--border)', borderRadius: 8, padding: 12, marginBottom: 12 }}>
            {FIELDS.map(({ key, label }) => (
              <div key={key} style={{ display: 'flex', justifyContent: 'space-between', padding: '4px 0', fontSize: '0.875rem' }}>
                <span style={{ color: 'var(--muted)' }}>{label}</span>
                <span style={{ fontWeight: 500 }}>{me?.[key] || (edits[key] || '').trim() || '—'}</span>
              </div>
            ))}
          </div>
          <div style={{ fontWeight: 700, fontSize: '1.1rem', marginBottom: 12 }}>
            {paid ? `Total: ${priceLabel}` : K.freeLabel}
          </div>
          {errMsg && <div style={{ color: '#991b1b', fontSize: '0.875rem', marginBottom: 8 }}>{errMsg}</div>}
          <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
            <button className="btn" onClick={() => setStep(1)} disabled={submitting}>← Back</button>
            <button className="btn btn-primary" onClick={submit} disabled={submitting}>
              {submitting ? (paid ? 'Redirecting…' : K.busyCta) : (paid ? `Pay ${priceLabel}` : K.confirmCta)}
            </button>
          </div>
        </Section>
      )}
    </div>
  );
}

function isBlank(s) { return s == null || String(s).trim() === ''; }

function autoCompleteFor(key) {
  switch (key) {
    case 'firstname': return 'given-name';
    case 'lastname':  return 'family-name';
    case 'email':     return 'email';
    case 'phone':     return 'tel';
    default:          return 'off';
  }
}
